import { ControlledDaDataFullNameAutocomplete } from '@sravni/cosago-react-library/lib/components';
import type { IFieldFactoryProps } from '@sravni/cosago-react-library/lib/types';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { capitalizeFullName } from 'shared/lib/formatters';

import { FullNameAutocompleteTexts } from './FullNameAutocomplete.texts';

type TProps = Omit<ComponentProps<typeof ControlledDaDataFullNameAutocomplete>, 'getCleanFullName' | 'name' | 'label'> &
  IFieldFactoryProps;

export const FullNameAutocomplete = ({ label, type, onError, isClearable = true, suggestions, ...props }: TProps) => {
  const { setError, clearErrors, watch, setValue } = useFormContext();
  const currentValue = watch(type);

  const handleSetWrongCharsetError = (error: Nullable<keyof typeof FullNameAutocompleteTexts>) => {
    const errorText = error ? FullNameAutocompleteTexts[error] : null;
    if (errorText) {
      setError(type, { message: errorText });
    } else {
      clearErrors(type);
    }

    if (onError) {
      onError(error);
    }
  };

  const onGetCleanFullName: ComponentProps<typeof ControlledDaDataFullNameAutocomplete>['getCleanFullName'] =
    useCallback(
      (fio, cb) => {
        const capitalizedName = capitalizeFullName(fio);
        const selectedSuggestion = suggestions.find((item) => item.value === capitalizedName);
        cb({ label: capitalizedName, value: capitalizedName, data: selectedSuggestion?.data || {} });
      },
      [suggestions],
    );

  const onBlurHandler = () => {
    if (!currentValue?.value || !currentValue?.data) {
      // для пустого или невалидного значения, ничего не делаем
      return;
    }

    /**
     * у валидного значения, которое пользователь ввел руками - data === {},
     * следовательно, поле уже имеет саггесты и можно найти соответствующий саггест по введенному значения
     * */
    if (!Object.keys(currentValue.data).length) {
      onGetCleanFullName(currentValue.value, (newValue) => setValue(type, newValue));
    }
  };

  return (
    <ControlledDaDataFullNameAutocomplete
      {...props}
      onBlur={onBlurHandler}
      suggestions={suggestions}
      isClearable={isClearable}
      label={label ?? ''}
      name={type}
      getCleanFullName={onGetCleanFullName}
      onError={handleSetWrongCharsetError}
      disabled={props.isDisabled}
    />
  );
};
